// react
import { useMemo } from 'react';
import styled from 'styled-components';

// components
import { Title } from '@/components/atoms';
import { BackButton } from '@/components/molecules';

// models
import {
  CareCircle as CareCircleType,
  CareCircleMember,
  Relationship,
} from '@karehero/models';

/**
 * CareCircleMemberInfo renders the account settings page.
 */
export const CareCircleMemberInfo = ({
  careCircle,
  careCircleMember,
  relationships,
}: CareCircleMemberInfoProps) => {
  // memos
  const careRecipientName = useMemo(() => {
    let name = `${careCircle?.careRecipientAccount?.firstName || ''}`;
    if (careCircle?.careRecipientAccount?.lastName) {
      name += ` ${careCircle?.careRecipientAccount?.lastName || ''}`;
    }
    return name;
  }, [careCircle]);

  const memberName = useMemo(() => {
    let name = `${careCircleMember?.account?.firstName || ''}`;
    if (careCircleMember?.account?.lastName) {
      name += ` ${careCircleMember?.account?.lastName || ''}`;
    }
    return name;
  }, [careCircleMember]);

  const careRecipientRelationship = useMemo(
    () =>
      relationships?.find(
        (r: Relationship) =>
          r.id === careCircleMember?.careRecipientAccountRelationshipId,
      ),
    [careCircleMember],
  );

  return (
    <Wrapper>
      <BackButton />
      <Title>{memberName || 'Care recipient'}'s Profile</Title>
      <AccountWrapper>
        <Field>
          <SubTitle>Name</SubTitle>
          <HelperText>{memberName}</HelperText>
        </Field>

        <Field>
          <SubTitle>Email address</SubTitle>
          <HelperText>{careCircleMember?.account?.email}</HelperText>
        </Field>

        <Field>
          <SubTitle>Relationship to {careRecipientName}</SubTitle>
          <HelperText>
            {careRecipientRelationship?.name || 'No relationship'}
          </HelperText>
        </Field>
      </AccountWrapper>
    </Wrapper>
  );
};

// styles
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding-bottom: ${(props) => props.theme.spacing[16]};
`;

const AccountWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[48]};
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[8]};
  align-items: flex-start;
`;

const SubTitle = styled.div`
  font: ${(props) => props.theme.typography.body.md.semibold};
  color: ${(props) => props.theme.color.neutral[90]};
`;

const HelperText = styled.div`
  font: ${(props) => props.theme.typography.body.md.regular};
  color: ${(props) => props.theme.color.neutral[80]};
`;

// types
interface CareCircleMemberInfoProps {
  careCircle?: CareCircleType;
  careCircleMember?: CareCircleMember;
  relationships: Relationship[];
}

CareCircleMemberInfo.displayName = 'CareCircleMemberInfo';

export default CareCircleMemberInfo;
